// composerAttachments — the PURE projection that turns the transient ingest
// signals into the composer's attachment chips (F83).
//
// The optimistic model already exists across the shell; this helper is the
// missing LINKAGE + PROJECTION, not a new store. It folds two transient
// sources into one chip list, each carrying its lifecycle `status`:
//   • the ingest batch (`IngestState.batch`, keyed by IngestId) — a `pending`
//     entry is an `uploading` chip, a `failed` entry a `failed` chip. These
//     exist the instant a file drops (the decider folds `files_queued`
//     synchronously), so the chip shows with NO server round-trip.
//   • the nascent conversation's pending grounding (`pendingGrounding`) — the
//     REAL, landed sourceId chips a nascent conversation already holds.
//
// A landed source is `ready`: its text is readable the moment the upload
// answers, so the agent can use it, unless its durable read status says its
// scanned pages are still being read (OCR) or failed (`landedStatusOf`). The background understanding sweep
// (summary, outline, notes) is not a usability gate and has no server fact the
// client could project, so the chip shows no "analysing" beat (D57: a
// client-tracked beat stuck on after the turn ended).
//
// Dedup (IngestId → sourceId, exactly once): when a file lands it BOTH settles
// its ingest entry to `done` (carrying the new sourceId) AND grounds that
// source (groundAddedSource → pendingGrounding). The grounded chip then
// represents it, so the ingest `done` twin is dropped here — the optimistic
// `ingest:<id>` chip is REPLACED by the real sourceId chip once, never shown
// alongside it.
//
// Linkage: an ingest entry belongs to a conversation only if the shell recorded
// that association at drop/pick time (`ingestConversations`). A drop with no
// active conversation records no linkage, so it yields no composer chip — the
// rail-only behaviour is preserved.
//
// Pure: no I/O, no clock, no React. VIEW-TRANSIENT by construction — every
// status is derived from transient sets the durable `file_landed` event
// reconciles; nothing here is written to the event log.

import { exhaustSwitchCase } from "@bilda/utils";
import type {
    ComposerAttachmentStatus,
    ComposerResourceKind,
    ComposerResourceRef,
} from "../components/Composer/PowerComposer/types.js";
import type { ResourceRef } from "../components/Navigation/types.js";
import type { IngestFile, IngestId } from "./ingest.js";
import {
    type PendingGrounding,
    pendingGroundingFor,
} from "./pendingGrounding.js";

// The stable, collision-free chip id for an OPTIMISTIC (not-yet-landed) upload
// — keyed by its per-session IngestId, never a real ResourceId, so it can never
// be confused with a landed source and React swaps it for the real sourceId
// chip exactly once the source lands.
export const optimisticChipId = (id: IngestId): string => `ingest:${id}`;

// The composer's kind noun for a ResourceRef (the one place the two sums
// disagree: "export" here reads as "package"). Mirrors `toComposerResourceRef`
// (constants.ts), inlined so this stays a pure, shell-free module.
const composerKind = (ref: ResourceRef): ComposerResourceKind =>
    ref.type === "export" ? "package" : ref.type;

// The composer chips for ONE conversation, folded from the transient sources.
// Grounded (landed) sources come first as the real sourceId chips; the
// optimistic ingest chips (still uploading / failed, or landed-but-not-yet-
// grounded) follow.
export const composerAttachmentsFor = (
    conversationId: string,
    ingestBatch: readonly IngestFile[],
    ingestConversations: ReadonlyMap<IngestId, string>,
    pendingGrounding: PendingGrounding,
    // A landed source's read status as a chip decoration (still reading its
    // scanned pages, or failed); absent ⇒ every landed source rests `ready`.
    landedStatusOf: (
        sourceId: string,
    ) => ComposerAttachmentStatus | undefined = () => undefined,
): readonly ComposerResourceRef[] => {
    // The landed, grounded sources this conversation already holds — the REAL
    // sourceId chips, resting `ready`.
    const grounded = pendingGroundingFor(pendingGrounding, conversationId);
    const groundedIds = new Set(grounded.map((r) => r.id));
    const groundedChips: readonly ComposerResourceRef[] = grounded.map(
        (r): ComposerResourceRef => ({
            kind: composerKind(r),
            id: r.id,
            title: r.title,
            status: landedStatusOf(r.id) ?? "ready",
        }),
    );

    // The ingest batch entries THIS conversation owns (recorded at drop/pick
    // time). Rejections surface on the ingest rail, never the composer.
    const ingestChips: readonly ComposerResourceRef[] = ingestBatch.flatMap(
        (f): readonly ComposerResourceRef[] => {
            if (ingestConversations.get(f.id) !== conversationId) return [];
            switch (f.type) {
                case "pending":
                    return [
                        {
                            kind: "source",
                            id: optimisticChipId(f.id),
                            title: f.filename,
                            status: "uploading",
                        },
                    ];
                case "failed":
                    return [
                        {
                            kind: "source",
                            id: optimisticChipId(f.id),
                            title: f.filename,
                            status: "failed",
                        },
                    ];
                case "rejected":
                    return [];
                case "done": {
                    // Dedup: the grounded chip above already represents a landed
                    // source (groundAddedSource grounds it as it settles), so
                    // drop the ingest twin — the optimistic chip is replaced,
                    // not duplicated. A landed-but-not-yet-grounded entry (the
                    // brief race, or a durable conversation that grounds into
                    // Context rather than pendingGrounding) still shows here.
                    const sourceId = f.source.sourceId;
                    if (groundedIds.has(sourceId)) return [];
                    return [
                        {
                            kind: "source",
                            id: sourceId,
                            title: f.source.title,
                            status: landedStatusOf(sourceId) ?? "ready",
                        },
                    ];
                }
                default:
                    return exhaustSwitchCase(f);
            }
        },
    );

    return [...groundedChips, ...ingestChips];
};

// The attachment chips a SEND carries for one conversation: its composer's
// attachments (pending grounding + drop-linked uploads), minus failed uploads
// (they attached nothing) and minus any id the draft already @-picked. An
// uploading chip keeps `status: "uploading"` — the decider holds the port send
// until it lands; every other status is render-time decoration, stripped so
// the sent mention stays a plain stamp.
export const sentAttachmentsFor = (
    conversationId: string,
    ingestBatch: readonly IngestFile[],
    ingestConversations: ReadonlyMap<IngestId, string>,
    pendingGrounding: PendingGrounding,
    composerMentions: readonly ComposerResourceRef[],
): readonly ComposerResourceRef[] =>
    composerAttachmentsFor(
        conversationId,
        ingestBatch,
        ingestConversations,
        pendingGrounding,
    )
        .filter((ref) => ref.status !== "failed")
        .filter((ref) => !composerMentions.some((m) => m.id === ref.id))
        .map(
            ({ status, ...ref }): ComposerResourceRef =>
                status === "uploading" ? { ...ref, status } : ref,
        );
